import { useMemo } from 'react';
import { AppState, KanbanTask, CalendarEvent, DataScope } from '@/types';

/**
 * 🏛️ useOmniSearch Hook v2.0
 * Refactored for Hybrid Scoping: Partition-aware indexing across Personal and Shared silos.
 */
export const useOmniSearch = (query: string, state: AppState, activeScope: DataScope = 'BOTH') => {
  return useMemo(() => {
    const q = query.toLowerCase().trim();
    const uid = state.currentUser?.id;
    const hid = state.currentHouseholdId;
    
    // Default return state for empty queries
    if (!q) {
      return { 
        results: [], 
        total: 0, 
        grouped: { calendar: [], tasks: [], maintenance: [] } 
      };
    }

    /**
     * 🛡️ HYBRID SCOPE FILTER
     * Ensures results only appear if they match the active toggle and 
     * user-ownership (for personal items) or membership (for shared items).
     */
    const applyScopeFilter = (item: any) => {
      const isPersonal = item.scope === 'personal' || !item.scope;
      const isShared = item.scope === 'household';

      // 1. If searching Personal scope
      if (activeScope === 'PERSONAL') {
        return isPersonal && item.ownerId === uid;
      }

      // 2. If searching Shared scope
      if (activeScope === 'SHARED') {
        // Enforce shared data privacy: must be in a household to see household items
        return isShared && !!hid;
      }

      // 3. 'BOTH' Scope
      const matchesPersonal = isPersonal && item.ownerId === uid;
      const matchesShared = isShared && !!hid;
      
      return matchesPersonal || matchesShared;
    };

    // 1. 🗓️ Filter Calendar Events
    const allCalendarEvents = [...(state.personalEvents || []), ...(state.householdEvents || [])];
    const calendar = allCalendarEvents
      .filter((e: CalendarEvent) => 
        (e.title.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q)) &&
        applyScopeFilter(e)
      )
      .map((e: CalendarEvent) => ({ ...e, resultType: 'event' as const }));

    // 2. 📋 Filter Kanban Tasks
    const tasks = (state.tasks || [])
      .filter((t: KanbanTask) => 
        (t.title.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)) &&
        applyScopeFilter(t)
      )
      .map((t: KanbanTask) => ({ ...t, resultType: 'task' as const }));

    // 3. 🛠️ Filter Maintenance Protocols
    const maintenance = (state.maintenanceTasks || [])
      .filter((m: KanbanTask) => 
        (m.title.toLowerCase().includes(q) || 
         m.description?.toLowerCase().includes(q) ||
         m.tags?.some((tag: string) => tag.toLowerCase().includes(q))) &&
        applyScopeFilter(m)
      )
      .map((m: KanbanTask) => ({ ...m, resultType: 'maintenance' as const }));

    // 🛰️ Unified Results Set
    const results = [...calendar, ...tasks, ...maintenance];

    return {
      results,
      total: results.length,
      grouped: { 
        calendar, 
        tasks, 
        maintenance 
      }
    };
  }, [query, state, activeScope]);
};